import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Done, VisibilityOff } from "@material-ui/icons";
import ReactMarkdown from "react-markdown";
import { api } from "../../services/apiService";
import { sanitizeTableHtml } from "../visuals-review/table-preview-logic";
import Menu from "../menu/menu";
import {
  RATING,
  DIMENSIONS,
  SHORTCUT,
  groupBySubject,
  visualKindLabel,
  isKnownVisualKind,
  showVisualKind,
  presentVisualKinds,
  filterGroupsByKind,
  normalizeVisualKind,
  buildSuitabilityRequest,
  suitabilityOutcome,
  resolveShortcut,
  isEditableTarget,
  groupItemIds,
  GROUP_STATE,
  groupReviewState,
  isGroupReadOnly,
  splitGroupsByState,
  filterDecidedItems,
  flattenQuestionsResponse,
  hasQuestionsPayload,
  myRatingGlyph,
  savedRating,
  ratingLabel,
  ratingToVoteBody,
  isItemFinalized,
  isItemPending,
  isCandidateEditable,
  isAuthorizedToRate,
  bestItemId,
  buildResetRequest,
  resetOutcome,
  groupProgress,
  modelInLoopSummary,
  metadataRows,
  currentAssigneeIds,
  normalizeRoster,
  searchRoster,
  assigneeLabel,
  assignmentOutcome,
  moreQuestionsOnServer,
  extractNeedsGeneration,
  topUpStatusLabel,
  adaptQuestionResponse,
  REJECT_CATEGORIES,
  decisionOutcome,
  buildGroupDecision,
  countDangerFlagged,
  winnerDangerFlagged,
  PLACEMENT,
  groupPlacement,
  groundingReferences,
  hasGrounding,
  groundingNoteText,
  referenceRoleChips,
  railKindDisplay,
} from "./review-queue-logic";
import "./review-queue.scss";

// One-line definitions so the five tags stay distinct in reviewers' minds —
// sharper tags = sharper training signal for the image pipeline.
const DIMENSION_HINTS = {
  accuracy: "The anatomy / medical facts are correct",
  labeling: "Labels point to the right structures",
  clarity: "Easy to read and understand at a glance",
  artifacts: "Free of AI glitches, warped shapes, or garbled text",
  relevance: "Shows what the question actually tests",
};

const EXAM_TYPE = "ABSITE";
const REVIEW_TYPE = "IMAGE_CANDIDATE";
// One page pulls enough items to keep each question's candidates together
// (grouping is client-side). The pilot pool is ~26 candidates; 100 covers it.
const PAGE_SIZE = 100;

// S1.2/P2-6: the mobile-comparison breakpoint — MUST track $rq-mobile-bp in
// review-queue.scss (single mobile cutoff for the whole page).
const MOBILE_MAX_WIDTH = 640;

// Live viewport flag for the honest mobile comparison model. Same guarded matchMedia
// pattern as the rail's auto-collapse; environments without matchMedia (tests) keep the
// initial innerWidth-derived value.
function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_MAX_WIDTH
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const onChange = (e) => setMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return mobile;
}

// Candidate images are served only through the role-gated proxy, which needs the
// Bearer header — a plain <img src> can't load them. Fetch the bytes (the app's
// patched fetch injects the token), then render an object URL. Rendered LARGE with
// click-to-zoom; `onZoom` hands the loaded src up so the page can show a lightbox.
function CandidateImage({ itemId, alt, onZoom }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let objectUrl;
    let active = true;
    setFailed(false);
    setSrc(null);
    api
      .getReviewItemImage(itemId)
      .then((objUrl) => {
        if (!active) {
          URL.revokeObjectURL(objUrl);
          return;
        }
        objectUrl = objUrl;
        setSrc(objUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [itemId, retry]);

  if (failed) {
    return (
      <div className="rq-img rq-img--failed">
        <span>Image unavailable</span>
        <Button
          size="small"
          variant="outlined"
          className="rq-img-retry"
          onClick={() => setRetry((r) => r + 1)}
        >
          Retry
        </Button>
      </div>
    );
  }
  if (!src) return <div className="rq-img rq-img--loading"><CircularProgress size={22} /></div>;
  return (
    <button
      type="button"
      className="rq-img-btn"
      title="Click to zoom"
      onClick={(e) => onZoom && onZoom(src, alt, e.currentTarget)}
    >
      <img className="rq-img" src={src} alt={alt || "candidate visual"} />
      <span className="rq-img-zoomhint">🔍 click to enlarge</span>
    </button>
  );
}

// S1.2/P2-6: one thumbnail in the mobile comparison strip — the strip keeps ALL candidates
// visible (with selected state) while the reviewer judges ONE full-width focused card, so
// comparison context never leaves a 390px screen (three side-by-side 3400×2400 diagrams at
// ~120px each would be simultaneity theater). Same role-gated bytes proxy as CandidateImage;
// a table candidate (or an unloaded/failed image) falls back to its number tile.
function CandidateThumb({ candidate, index, total, current, onFocus }) {
  const p = (candidate && candidate.payload) || {};
  const isTable = Boolean(p.tableContent || p.tableHtml);
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (isTable) return undefined;
    let objectUrl;
    let active = true;
    setSrc(null);
    api
      .getReviewItemImage(candidate.id)
      .then((objUrl) => {
        if (!active) {
          URL.revokeObjectURL(objUrl);
          return;
        }
        objectUrl = objUrl;
        setSrc(objUrl);
      })
      .catch(() => {}); // fallback number tile — the focused card owns the loud failure
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [candidate.id, isTable]);
  return (
    <button
      type="button"
      className={`rq-thumb-btn${current ? " rq-thumb-btn--current" : ""}`}
      aria-label={`Focus candidate ${index + 1} of ${total}`}
      aria-pressed={current}
      onClick={onFocus}
    >
      {src
        ? <img className="rq-thumb-img" src={src} alt="" />
        : <span className="rq-thumb-num">{isTable ? "⊞" : index + 1}</span>}
      <span className="rq-thumb-idx" aria-hidden="true">{index + 1}</span>
    </button>
  );
}

// S1.2/P2-9: the ONE loud-failure banner (warning-styled, role="alert", REQUIRED Retry) —
// the shared component behind every "grounding surface failed to load" state: the
// bank-fetch failure in QuestionDetail and the reference-image byte failure (R-V4-A9
// named them the same banner class; this is that class, consolidated). Never a quiet
// degrade note — an ungrounded judge is the exact failure the correction exists to kill.
function LoudFailure({ message, onRetry, className = "" }) {
  return (
    <div className={`rq-loudfail ${className}`} role="alert">
      <span className="rq-loudfail-msg">⚠ {message}</span>
      <Button
        size="small"
        variant="outlined"
        className="rq-loudfail-retry"
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

// S3-U1 / R-P1-1: a grounding reference image, served through the dedicated role-gated
// proxy (keyed by assetId, Bearer-injected fetch → object URL — same pattern as
// CandidateImage). On byte-load failure this renders the shared LOUD failure tile (never a
// silently-missing image / empty broken pane, #627 class) with a REQUIRED Retry that
// refetches (R-V4-A9, full parity with the bank-fetch loud-banner + Retry rule). The
// caption + citation are rendered by the caller OUTSIDE this tile, so they survive a byte
// failure (metadata is not byte-dependent).
function ReferenceImage({ assetId, alt, onZoom }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let objectUrl;
    let active = true;
    setFailed(false);
    setSrc(null);
    api
      .getReferenceAssetImage(assetId)
      .then((objUrl) => {
        if (!active) {
          URL.revokeObjectURL(objUrl);
          return;
        }
        objectUrl = objUrl;
        setSrc(objUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, retry]);

  if (failed) {
    return (
      <LoudFailure
        className="rq-ref-img rq-ref-img--failed"
        message="Reference image unavailable"
        onRetry={() => setRetry((r) => r + 1)}
      />
    );
  }
  if (!src) return <div className="rq-ref-img rq-ref-img--loading"><CircularProgress size={18} /></div>;
  return (
    <button
      type="button"
      className="rq-ref-img-btn"
      title="Click to zoom"
      onClick={(e) => onZoom && onZoom(src, alt, e.currentTarget)}
    >
      <img className="rq-ref-img" src={src} alt={alt || "reference image"} />
      <span className="rq-ref-img-zoomhint">🔍 enlarge</span>
    </button>
  );
}

// S3-U1 / S2-addendum D4 + R-V4-A9: the "Reference" strip beside the candidate grid. Shows the
// question's source-image ground truth (image + caption + citation), zoomable via the existing
// lightbox. SHARED by tutor and admin (parity). Collapsible, EXPANDED by default when references
// exist (same visibility principle as S1-U2's explanation). When there are NO references it
// renders the compact none-on-file note instead of an empty pane (#627 class); admins additionally
// see the derived pipeline-compliance STATE CHIP (groundingStatus — admin-only, tutors never get
// the field). Generation-role chips are NOT here — they live in the admin-only metadata panel.
function ReferencePanel({ group, isAdmin, onZoom }) {
  const refs = groundingReferences(group);
  const [open, setOpen] = useState(true);
  const grounded = hasGrounding(group);

  if (!grounded) {
    const note = groundingNoteText(group);
    return (
      <div className="rq-ref rq-ref--none">
        <span className="rq-ref-note">{note}</span>
        {isAdmin && group && group.groundingStatus && (
          <Chip size="small" label={group.groundingStatus} className="rq-ref-state-chip" />
        )}
      </div>
    );
  }

  return (
    <div className="rq-ref">
      <div className="rq-ref-head">
        <Button
          size="small"
          variant="text"
          className="rq-ref-toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "▾" : "▸"} Reference{refs.length > 1 ? `s (${refs.length})` : ""}
        </Button>
        {isAdmin && group && group.groundingStatus && (
          <Chip size="small" label={group.groundingStatus} className="rq-ref-state-chip" />
        )}
      </div>
      {open && (
        <div className="rq-ref-strip">
          {refs.map((r) => (
            <figure key={r.assetId} className="rq-ref-cell">
              <ReferenceImage assetId={r.assetId} alt={r.caption} onZoom={onZoom} />
              <figcaption className="rq-ref-meta">
                {r.caption && <span className="rq-ref-caption">{r.caption}</span>}
                {r.citation && <span className="rq-ref-citation">{r.citation}</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}

// The question's raw 24-hex id means nothing to a human reviewer, so the badge
// shows a short de-emphasized tail; the full id is one click away (copy).
function QuestionIdBadge({ id }) {
  const [copied, setCopied] = useState(false);
  const full = String(id || "");
  const tail = full.length > 8 ? `…${full.slice(-6)}` : full;
  const copy = () => {
    if (!(navigator.clipboard && navigator.clipboard.writeText)) return;
    navigator.clipboard
      .writeText(full)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };
  return (
    <Tooltip title={copied ? "Copied!" : `${full} — click to copy the full id`} placement="top">
      <button type="button" className="rq-id" onClick={copy}>
        id {tail.replace("…", "")}
      </button>
    </Tooltip>
  );
}

// A candidate's body: a TABLE (server-sanitized HTML rendered natively via the
// shared XSS-safe sanitizer) or an IMAGE, driven by the payload.
function CandidateBody({ candidate, onZoom }) {
  const p = (candidate && candidate.payload) || {};
  const tableHtml = p.tableContent || p.tableHtml || null;
  if (tableHtml) {
    return (
      <div className="rq-table-wrap">
        <div className="rq-table" dangerouslySetInnerHTML={{ __html: sanitizeTableHtml(tableHtml) }} />
      </div>
    );
  }
  return <CandidateImage itemId={candidate.id} alt={p.altText} onZoom={onZoom} />;
}

// The BASE question shown ABOVE the candidates so a reviewer judges a proposed
// visual against the real question — a condensed mirror of the student exam-review
// panel (stem + choices with the correct one marked + explanation + key-points).
// Read-only via the existing bank endpoint; dark-safe; explanation collapsible.
function QuestionDetail({ subjectKey }) {
  const [state, setState] = useState({ status: "loading", q: null });
  // S1-U2 (IMAGE-REVIEW-PRODUCT-CORRECTION §5): the medical explanation + key points are in
  // front of the reviewer BY DEFAULT for ALL roles (owner audit; reverses the 2026-07-10
  // default-collapsed R2 call). The toggle still collapses within the session; the default
  // re-applies on every question so navigation never leaves the reader collapsed. No
  // localStorage persistence — nothing to give a lifecycle (REVIEW-SCOPE rule), so there is
  // no client-persisted preference to reason about.
  const [showExpl, setShowExpl] = useState(true);
  // P2-9: a bank-fetch failure is retryable — bumping re-runs the effect (the same
  // affordance as every other loud-failure surface).
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ status: "loading", q: null });
    setShowExpl(true);
    api
      .getQuestionsByIds([subjectKey])
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res)
          ? res
          : (res && (res.questions || res.question_list)) || [];
        const q = list.find((x) => x && (x.id === subjectKey || x._id === subjectKey)) || list[0] || null;
        setState(q ? { status: "ok", q } : { status: "unavailable", q: null });
      })
      .catch(() => active && setState({ status: "unavailable", q: null }));
    return () => { active = false; };
  }, [subjectKey, retry]);

  if (state.status === "loading") {
    return <div className="rq-qdetail rq-qdetail--loading"><CircularProgress size={20} /></div>;
  }
  if (state.status !== "ok") {
    // P2-9: the old quiet "judge the candidates on the caption + image" degrade note WAS the
    // defect — it invited exactly the ungrounded judging this queue exists to eliminate. The
    // failure is now the shared LOUD banner + Retry (same class as the reference tile).
    return (
      <div className="rq-qdetail rq-qdetail--failed">
        <LoudFailure
          className="rq-qdetail-fail"
          message="Base question could not be loaded — candidates should be judged against the real question, not from memory."
          onRetry={() => setRetry((r) => r + 1)}
        />
      </div>
    );
  }
  const q = state.q;
  const str = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
  const stem = str(q.stem || q.questionText || q.question);
  const options = Array.isArray(q.option) ? q.option : Array.isArray(q.options) ? q.options : [];
  const answerKey = q.answerKey || q.answer || null;
  const explanation = str(q.explanation);
  const keyPoints = str(q.briefExplanation || q.keyPoints);
  const hasExpl = Boolean(explanation || keyPoints);
  return (
    <div className="rq-qdetail">
      <Typography variant="subtitle2" className="rq-qdetail-label">Base question (reference)</Typography>
      {stem
        ? <Typography variant="body1" className="rq-qdetail-stem">{stem}</Typography>
        : <Typography variant="caption" className="rq-qdetail-note">No stem text on this question.</Typography>}
      {options.length > 0 && (
        <ul className="rq-qdetail-options">
          {options.map((o, i) => {
            const key = o && (o.key || o.label || o.id || String.fromCharCode(65 + i));
            const rawText = typeof o === "string" ? o : (o && (o.text || o.value || o.option)) || "";
            const text = typeof rawText === "string" ? rawText : String(rawText);
            const isAns = answerKey != null && String(key) === String(answerKey);
            return (
              <li key={i} className={`rq-qdetail-opt ${isAns ? "rq-qdetail-opt--answer" : ""}`}>
                <span className="rq-qdetail-optkey">{key}</span>
                <span className="rq-qdetail-opttext">{text}</span>
                {isAns && <span className="rq-qdetail-answermark">✓ answer</span>}
              </li>
            );
          })}
        </ul>
      )}
      {hasExpl && (
        <div className="rq-qdetail-expl">
          <Button
            size="small"
            variant="text"
            className="rq-qdetail-toggle"
            onClick={() => setShowExpl((s) => !s)}
          >
            {showExpl ? "▾ Hide explanation" : "▸ Show explanation & key points"}
          </Button>
          {showExpl && (
            // P1-5 layout guard: expanded BY DEFAULT (S1-U2 preserved) but inside a
            // bounded-height internal-scroll wrapper — ABSITE explanations run long, and
            // unbounded expansion pushed the judging grid below the fold (scroll tax on
            // every question). The full text stays reachable by scrolling in place.
            <div className="rq-qdetail-explscroll">
              {explanation && (
                <div className="rq-qdetail-explbody">
                  <ReactMarkdown>{explanation}</ReactMarkdown>
                </div>
              )}
              {keyPoints && (
                <div className="rq-qdetail-keypoints">
                  <ReactMarkdown>{keyPoints}</ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// S5/P0-2/A4: the answer-leakage placement banner. A prominent per-question line telling the
// reviewer WHERE the image will appear — the fact they must judge answer leakage against.
// Rendered for BOTH roles (placement is not sensitive). STEM = warning-styled (the leak-risk
// case, shown before answering); EXPLANATION = neutral. Uses leak-safe dominance: if any
// candidate is STEM the whole group shows the STEM warning (groupPlacement).
function PlacementBanner({ group }) {
  const isStem = groupPlacement(group) === PLACEMENT.STEM;
  return (
    <div
      className={`rq-placement ${isStem ? "rq-placement--stem" : "rq-placement--expl"}`}
      role={isStem ? "alert" : undefined}
    >
      <span className="rq-placement-icon" aria-hidden="true">{isStem ? "⚠" : "ℹ"}</span>
      <span className="rq-placement-text">
        {isStem
          ? "Shown WITH the question before answering — must not reveal the answer"
          : "Shown in the explanation after answering"}
      </span>
    </div>
  );
}

// Per-question suitability toggle: "no image and/or no table belongs on this
// question at all" — an EXCLUSION signal about the question, independent of the
// candidate ratings. Collapsed by default; posts independently. Retained from the
// compare page (orthogonal to the rating rework).
function SuitabilitySection({ subjectKey, disabled, kindHint, onSuitability, onComplete, onBusyChange, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState(false);
  const [table, setTable] = useState(false);
  const [imageReason, setImageReason] = useState("");
  const [tableReason, setTableReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notEnabled, setNotEnabled] = useState(false);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open, onOpenChange]);

  const close = () => {
    setOpen(false);
    setError(null);
    setNotEnabled(false);
  };

  const save = () => {
    if (disabled) return;
    if (!image && !table) {
      setError("Tick “no image” and/or “no table”, or cancel.");
      return;
    }
    const state = {};
    if (image) {
      state.image = true;
      state.imageReason = imageReason;
    }
    if (table) {
      state.table = true;
      state.tableReason = tableReason;
    }
    setBusy(true);
    if (onBusyChange) onBusyChange(true);
    setError(null);
    setNotEnabled(false);
    onSuitability(subjectKey, state)
      .then((outcome) => {
        if (outcome === "ok") {
          onComplete();
        } else if (outcome === "dark") {
          setNotEnabled(true);
        } else {
          setError("Couldn’t save — please retry.");
        }
      })
      .finally(() => {
        setBusy(false);
        if (onBusyChange) onBusyChange(false);
      });
  };

  if (!open) {
    return (
      <div className="rq-suit-toggle">
        <Button
          size="small"
          variant="text"
          className="rq-suit-open"
          startIcon={<VisibilityOff fontSize="small" />}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          This question doesn’t need a visual
        </Button>
      </div>
    );
  }

  const imageCheck = (
    <>
      <FormControlLabel
        key="image"
        className="rq-suit-check"
        control={<Checkbox size="small" checked={image} onChange={(e) => setImage(e.target.checked)} />}
        label={`No image or diagram belongs on this question${kindHint === "table" ? " (even if none are shown here)" : ""}`}
      />
      {image && (
        <TextField
          className="rq-suit-reason"
          placeholder="Why not? (optional)"
          multiline
          minRows={1}
          variant="outlined"
          fullWidth
          value={imageReason}
          onChange={(e) => setImageReason(e.target.value)}
        />
      )}
    </>
  );
  const tableCheck = (
    <>
      <FormControlLabel
        key="table"
        className="rq-suit-check"
        control={<Checkbox size="small" checked={table} onChange={(e) => setTable(e.target.checked)} />}
        label={`No table belongs on this question${kindHint === "table" ? "" : " (even if none are shown here)"}`}
      />
      {table && (
        <TextField
          className="rq-suit-reason"
          placeholder="Why not? (optional)"
          multiline
          minRows={1}
          variant="outlined"
          fullWidth
          value={tableReason}
          onChange={(e) => setTableReason(e.target.value)}
        />
      )}
    </>
  );

  return (
    <div className="rq-suit">
      <Typography variant="body2" className="rq-suit-head">
        If this question shouldn’t have a visual at all, mark it here — QLearn will stop
        proposing those candidates for it. You can undo this later.
      </Typography>
      {kindHint === "table" ? (
        <>
          {tableCheck}
          {imageCheck}
        </>
      ) : (
        <>
          {imageCheck}
          {tableCheck}
        </>
      )}
      {error && (
        <Typography className="rq-gate" color="error" variant="caption">{error}</Typography>
      )}
      {notEnabled && (
        <Typography className="rq-suit-notice" variant="caption">
          The “not needed” marking isn’t enabled on this server yet, so it wasn’t saved.
          Your candidate ratings still count — you can Cancel this.
        </Typography>
      )}
      <div className="rq-actions">
        <Button variant="outlined" className="rq-suit-cancel" disabled={busy || disabled} onClick={close}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          disabled={busy || disabled || (!image && !table)}
          onClick={save}
        >
          {busy ? "Saving…" : "Mark unsuitable"}
        </Button>
      </div>
    </div>
  );
}

// Rigorous-metadata panel — ADMIN-ONLY (D2). Renders per-candidate provenance/model
// rows from the server-redacted response (client-side redaction was how the 3
// historical leaks happened; the server response IS the source of truth for which
// rows exist). Now iterates ALL candidates (AD-5), labelled by position.
function MetadataPanel({ group }) {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [group]);
  const candidates = ((group && group.candidates) || []).filter(Boolean);
  if (candidates.length === 0) return null;
  return (
    <div className="rq-meta">
      <Button size="small" variant="text" className="rq-meta-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? "▾ Metadata" : "▸ Metadata"}
      </Button>
      {open && candidates.map((c, i) => {
        const summary = modelInLoopSummary(c);
        const rows = metadataRows(c);
        // S3-U1 (D1/D5/R-V4-A7): admin-only per-candidate generation-role chips. The pipeline
        // DECLARES each reference's role in generation (CONDITIONING | STYLE) — labeled
        // "declared by pipeline" because these are producer-asserted, unverifiable claims (honesty
        // clause). Admin-only by construction: this panel is admin-gated AND the source map rides
        // inside the redactor-stripped provenance.
        const roleChips = referenceRoleChips(c);
        return (
          <div key={c.id || i} className="rq-meta-cand">
            <Typography variant="caption" className="rq-meta-head">
              Candidate {i + 1}
            </Typography>
            {summary && <Typography variant="caption" className="rq-meta-summary">{summary}</Typography>}
            <dl className="rq-meta-rows">
              {rows.map((r) => (
                <div key={r.label} className="rq-meta-row">
                  <dt>{r.label}</dt>
                  <dd>{r.value}</dd>
                </div>
              ))}
            </dl>
            {roleChips.length > 0 && (
              <div className="rq-meta-roles">
                <Typography variant="caption" className="rq-meta-roles-label">
                  Generation roles (declared by pipeline)
                </Typography>
                <div className="rq-meta-roles-chips">
                  {roleChips.map((rc) => (
                    <Chip
                      key={rc.assetId}
                      size="small"
                      label={rc.role}
                      title={`asset ${rc.assetId}`}
                      className="rq-meta-role-chip"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Status rail: every question in the queue with a live status — TO REVIEW and
// COMPLETED — click to jump. Rows derive state from each item's own reviewStatus.
function StatusRail({
  workGroups, completedGroups, currentKey, onSelectWork, onSelectCompleted,
  collapsed, onToggle, locked, progressLine, filterRow, isAdmin,
  needsGenerationGroups, onSelectNeedsGen,
}) {
  const needsGen = needsGenerationGroups || [];
  // S1-U1: one NEEDS-GENERATION rail row per held group — a backfill task, not review work.
  const needsGenRow = (e) => {
    const idTail = String(e.subjectKey || "").slice(-6);
    return (
      <li key={e.subjectKey}>
        <button
          type="button"
          className={`rq-rail-row rq-rail-row--needsgen ${e.subjectKey === currentKey ? "rq-rail-row--current" : ""}`}
          disabled={locked}
          onClick={() => onSelectNeedsGen(e.subjectKey)}
        >
          <span className="rq-rail-pos">{e.subjectKey === currentKey ? "▶" : "⚙"}</span>
          <span className="rq-rail-id">…{idTail}</span>
          <span className="rq-rail-needsgen-count">{e.pendingCount}/{e.floor}</span>
        </button>
      </li>
    );
  };
  const railRow = (g, idx, completed) => {
    const st = groupReviewState(g);
    const glyph = myRatingGlyph(g);
    const idTail = String(g.subjectKey || "").slice(-6);
    // T-R4: compact admin-only flagged count on the rail row (server aggregate; tutors never
    // receive dangerFlagged, so countDangerFlagged is 0 for them and the badge never renders).
    const dangerCount = isAdmin ? countDangerFlagged(g) : 0;
    return (
      <li key={g.subjectKey}>
        <button
          type="button"
          className={`rq-rail-row ${g.subjectKey === currentKey ? "rq-rail-row--current" : ""}`}
          disabled={locked}
          onClick={() => (completed ? onSelectCompleted(g.subjectKey) : onSelectWork(idx))}
        >
          <span className="rq-rail-pos">{completed ? (glyph || "✓") : g.subjectKey === currentKey ? "▶" : "●"}</span>
          <span className="rq-rail-id">…{idTail}</span>
          {/* P2-1: UNKNOWN collapses to a compact "?" marker in the rail (label noise at
              10+ rows); known kinds keep their label. Question-view chrome unchanged. */}
          {(() => {
            const kd = railKindDisplay(g.visualKind, isAdmin);
            if (!kd) return null;
            return kd.compact
              ? <span className="rq-rail-kind rq-rail-kind--unknown" title="Needs classification">?</span>
              : <span className="rq-rail-kind">{kd.text}</span>;
          })()}
          {dangerCount > 0 && (
            <span className="rq-rail-danger" title={`${dangerCount} candidate(s) flagged for a clinical-safety concern`}>⚑ {dangerCount}</span>
          )}
          {!completed && st.partial && <span className="rq-rail-partial">partial</span>}
          {completed && st.state === GROUP_STATE.COMPLETED && st.status && (
            <span className={`rq-rail-status rq-rail-status--${st.status.toLowerCase()}`}>{st.status.toLowerCase()}</span>
          )}
        </button>
      </li>
    );
  };
  if (collapsed) {
    const miniRow = (g, idx, completed) => {
      const st = groupReviewState(g);
      const glyph = myRatingGlyph(g);
      const idTail = String(g.subjectKey || "").slice(-6);
      const kind = showVisualKind(g.visualKind, isAdmin) ? ` · ${visualKindLabel(g.visualKind).toLowerCase()}` : "";
      const statusTag = completed && st.status ? ` · ${st.status.toLowerCase()}` : (!completed && st.partial ? " · partial" : "");
      const statusClass = completed && st.state === GROUP_STATE.COMPLETED && st.status
        ? ` rq-rail-mini-row--${st.status.toLowerCase()}` : "";
      // T-R4: keep the admin flagged count discoverable even in the collapsed rail (the rail
      // auto-collapses <1600px — a flagged group must not hide on a laptop).
      const dangerCount = isAdmin ? countDangerFlagged(g) : 0;
      const dangerTag = dangerCount > 0 ? ` · ⚑ ${dangerCount}` : "";
      return (
        <button
          key={g.subjectKey}
          type="button"
          className={`rq-rail-mini-row${g.subjectKey === currentKey ? " rq-rail-mini-row--current" : ""}${statusClass}${dangerCount > 0 ? " rq-rail-mini-row--danger" : ""}`}
          disabled={locked}
          title={`…${idTail}${kind}${statusTag}${dangerTag}`}
          onClick={() => (completed ? onSelectCompleted(g.subjectKey) : onSelectWork(idx))}
        >
          {completed ? (glyph || "✓") : g.subjectKey === currentKey ? "▶" : st.partial ? "◐" : "●"}
          {dangerCount > 0 && (
            <span className="rq-rail-mini-danger" title={`${dangerCount} candidate(s) flagged for a clinical-safety concern`}>⚑</span>
          )}
        </button>
      );
    };
    return (
      <aside className="rq-rail rq-rail--collapsed">
        {/* P2-4: the rail auto-collapses <1600px, hiding the NEEDS GENERATION section on
            typical laptops — badge the toggle so held groups stay discoverable. */}
        <button
          type="button"
          className="rq-rail-toggle"
          title={needsGen.length > 0
            ? `Show question list — ${needsGen.length} question(s) need generation`
            : "Show question list"}
          onClick={onToggle}
        >
          ☰
          {needsGen.length > 0 && (
            <span className="rq-rail-toggle-badge" title={`${needsGen.length} question(s) need generation`}>
              ⚙ {needsGen.length}
            </span>
          )}
        </button>
        <span className="rq-rail-mini" title={`${workGroups.length} to review`}>{workGroups.length}</span>
        <div className="rq-rail-mini-list">
          {workGroups.map((g, i) => miniRow(g, i, false))}
          {completedGroups.length > 0 && <span className="rq-rail-mini-sep" aria-hidden="true" />}
          {completedGroups.map((g, i) => miniRow(g, i, true))}
          {needsGen.length > 0 && <span className="rq-rail-mini-sep" aria-hidden="true" />}
          {needsGen.map((e) => (
            <button
              key={e.subjectKey}
              type="button"
              className={`rq-rail-mini-row rq-rail-mini-row--needsgen${e.subjectKey === currentKey ? " rq-rail-mini-row--current" : ""}`}
              disabled={locked}
              title={`…${String(e.subjectKey || "").slice(-6)} · needs generation · ${e.pendingCount}/${e.floor}`}
              onClick={() => onSelectNeedsGen(e.subjectKey)}
            >
              ⚙
            </button>
          ))}
        </div>
      </aside>
    );
  }
  return (
    <aside className="rq-rail">
      <div className="rq-rail-head">
        <Typography variant="subtitle2">Questions</Typography>
        <button type="button" className="rq-rail-toggle" title="Hide question list" onClick={onToggle}>
          ⟨
        </button>
      </div>
      {filterRow}
      <Typography variant="caption" className="rq-rail-group">TO REVIEW ({workGroups.length})</Typography>
      <ul className="rq-rail-list">{workGroups.map((g, i) => railRow(g, i, false))}</ul>
      {completedGroups.length > 0 && (
        <>
          <Typography variant="caption" className="rq-rail-group">COMPLETED ({completedGroups.length})</Typography>
          <ul className="rq-rail-list">{completedGroups.map((g, i) => railRow(g, i, true))}</ul>
        </>
      )}
      {needsGen.length > 0 && (
        <>
          <Typography variant="caption" className="rq-rail-group rq-rail-group--needsgen">
            NEEDS GENERATION ({needsGen.length})
          </Typography>
          <ul className="rq-rail-list">{needsGen.map((e) => needsGenRow(e))}</ul>
        </>
      )}
      {progressLine && <Typography variant="caption" className="rq-rail-progress">{progressLine}</Typography>}
    </aside>
  );
}

// The saved rating shown read-only (AD-3): a chip when the caller rated it, else a
// note. Used for finalized candidates and for unauthorized/read-only viewers.
function ReadOnlyRating({ saved, finalized }) {
  return (
    <div className="rq-rating rq-rating--readonly">
      {saved && saved.rating ? (
        <Chip
          size="small"
          className={`rq-rating-chip rq-rating-chip--${saved.rating === RATING.USABLE ? "usable" : "not"}`}
          label={`${ratingLabel(saved.rating)}${saved.isBest ? " · Best" : ""}`}
        />
      ) : (
        <Typography variant="caption" className="rq-rating-none">
          {finalized ? "Decision made — not rated by you" : "Not yet rated · read-only"}
        </Typography>
      )}
      {saved && saved.comment && (
        <Typography variant="caption" className="rq-rating-note-text">{saved.comment}</Typography>
      )}
    </div>
  );
}

// The per-candidate independent rating control (AD-1): usable / not-usable, optional
// tag chips + a note, and an optional "best of the set" flag (≤1 per question,
// enforced server-side). Seeded from the caller's saved rating; a Save posts the
// vote upsert. Rendered only when the candidate is editable (PENDING && authorized).
function RatingControl({ saved, onSave }) {
  const [rating, setRating] = useState(saved ? saved.rating : null);
  const [dims, setDims] = useState([]);
  const [note, setNote] = useState(saved ? saved.comment : "");
  const [isBest, setIsBest] = useState(saved ? saved.isBest : false);
  // P2-8: the admin per-candidate danger flag, initialized from the caller's own saved vote so
  // the ⚑ toggle is truthful. It rides the SAME vote body (ratingToVoteBody), server-mapped into
  // typeSignal symmetric with the comparative stampGroupSignal.
  const [dangerFlag, setDangerFlag] = useState(saved ? Boolean(saved.dangerFlag) : false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // AD-7 (save-is-final) unmounts this control the instant a save finalizes the
  // candidate (RatingControl → ReadOnlyRating) — BEFORE onSave's promise settles. Guard
  // the .then/.finally so they skip setState on the unmounted control (mirrors the
  // #629 P2.2 ResetDialog mountedRef).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const toggleDim = (d) => setDims((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  // Selected tags prefix the note so the axis stays machine-parseable; a bare note
  // with no tags is sent verbatim (so an empty tag set never mutates the comment).
  const comment = dims.length ? `[${dims.join(",")}] ${note}`.trim() : note;

  const save = () => {
    if (!rating) {
      setError("Pick usable or not usable.");
      return;
    }
    setBusy(true);
    setError(null);
    Promise.resolve(onSave(ratingToVoteBody({ rating, isBest, comment, dangerFlag })))
      .then((res) => {
        if (mountedRef.current && res && res.error) setError("Couldn’t save — please retry.");
      })
      .finally(() => {
        if (mountedRef.current) setBusy(false);
      });
  };

  return (
    <div className="rq-rating">
      <div className="rq-rating-choice">
        <Button
          size="small"
          className="rq-rate rq-rate--usable"
          variant={rating === RATING.USABLE ? "contained" : "outlined"}
          color={rating === RATING.USABLE ? "primary" : "default"}
          aria-pressed={rating === RATING.USABLE}
          onClick={() => setRating(RATING.USABLE)}
        >
          Usable
        </Button>
        <Button
          size="small"
          className={`rq-rate rq-rate--not ${rating === RATING.NOT_USABLE ? "rq-rate--not-on" : ""}`}
          variant={rating === RATING.NOT_USABLE ? "contained" : "outlined"}
          aria-pressed={rating === RATING.NOT_USABLE}
          onClick={() => setRating(RATING.NOT_USABLE)}
        >
          Not usable
        </Button>
      </div>
      <div className="rq-dims">
        {DIMENSIONS.map((d) => (
          <Tooltip key={d} title={DIMENSION_HINTS[d] || ""} placement="top">
            <Chip
              size="small"
              label={d}
              clickable
              icon={dims.includes(d) ? <Done /> : undefined}
              aria-pressed={dims.includes(d)}
              variant={dims.includes(d) ? "default" : "outlined"}
              className={`rq-dim ${dims.includes(d) ? "rq-dim--on" : ""}`}
              onClick={() => toggleDim(d)}
            />
          </Tooltip>
        ))}
      </div>
      <TextField
        className="rq-rating-note-input"
        placeholder="Add a note (optional)"
        multiline
        minRows={1}
        variant="outlined"
        fullWidth
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <FormControlLabel
        className="rq-best"
        control={<Checkbox size="small" checked={isBest} onChange={(e) => setIsBest(e.target.checked)} />}
        label="Best of the set"
      />
      <button
        type="button"
        title="Flag a clinical-safety concern with this image"
        aria-pressed={dangerFlag}
        className={`rq-rate-danger ${dangerFlag ? "rq-rate-danger--on" : ""}`}
        onClick={() => setDangerFlag((v) => !v)}
      >
        ⚑ {dangerFlag ? "Flagged: clinical-safety" : "Flag clinical-safety"}
      </button>
      {error && <Typography color="error" variant="caption" className="rq-gate">{error}</Typography>}
      <Button
        size="small"
        variant="contained"
        color="primary"
        className="rq-save-rating"
        disabled={busy || !rating}
        onClick={save}
      >
        {busy ? "Saving…" : "Save rating"}
      </Button>
    </div>
  );
}

// One candidate cell in the N-up grid. Renders the visual + either the editable
// per-candidate rating control (PENDING && authorized) or a read-only saved-rating
// chip (finalized, or an unauthorized/read-only viewer). No empty placeholder pane.
function CandidateCard({
  candidate, index, total, editable, showModel, bestId, onSave, onZoom,
  comparative, comparativeSelected, onComparativeSelect, dangerFlagged, onToggleDanger,
}) {
  const p = (candidate && candidate.payload) || {};
  const kind = (p.tableContent || p.tableHtml) ? "table" : "image";
  const saved = savedRating(candidate);
  const isBestPick = bestId === candidate.id;
  // A2 comparative mode (tutors): a PENDING cell offers select-as-best + the
  // per-candidate danger-flag escape hatch INSTEAD of a scorecard; decided cells keep
  // their read-only chip (AD-7).
  const comparativePending = comparative && isItemPending(candidate);
  return (
    <div className={`rq-col ${comparativeSelected ? "rq-col--picked" : ""}`}>
      <div className="rq-col-head">
        <span className="rq-label">{total > 1 ? `#${index + 1}` : "Candidate"}</span>
        <Chip size="small" label={kind} className="rq-kind-chip" />
        {isBestPick && <Chip size="small" label="★ Best" className="rq-chip-pick" />}
        {/* T-R4: admin-only danger badge — the server aggregate dangerFlagged (across all
            reviewers) surfaces the write-only safety flag on the review surface. Notes (no
            reviewer attribution) ride the tooltip. */}
        {showModel && candidate.dangerFlagged && (
          <Chip
            size="small"
            label="⚑ flagged"
            className="rq-chip-danger"
            title={(candidate.dangerNotes || []).join(" • ") || "Clinical-safety concern flagged"}
          />
        )}
        {showModel && <span className="rq-model">{p.model || "—"}</span>}
      </div>
      <CandidateBody candidate={candidate} onZoom={onZoom} />
      {p.caption && <Typography variant="caption" className="rq-caption">{p.caption}</Typography>}
      {comparativePending ? (
        <div className="rq-cmp-cell">
          <Button
            size="small"
            variant={comparativeSelected ? "contained" : "outlined"}
            color="primary"
            className="rq-cmp-select"
            onClick={() => onComparativeSelect(candidate.id)}
          >
            {comparativeSelected ? "Selected as best" : "Select as best"}
          </Button>
          <button
            type="button"
            title="Flag a clinical-safety concern with this image"
            aria-pressed={Boolean(dangerFlagged)}
            className={`rq-cmp-danger ${dangerFlagged ? "rq-cmp-danger--on" : ""}`}
            onClick={() => onToggleDanger(candidate.id)}
          >
            ⚑
          </button>
        </div>
      ) : editable ? (
        <RatingControl saved={saved} onSave={onSave} />
      ) : (
        <ReadOnlyRating saved={saved} finalized={isItemFinalized(candidate)} />
      )}
    </div>
  );
}

// The A2 tutor task: ONE comparative decision per question group — pick the best
// candidate OR reject the whole set with a required structured reason (category +
// one line, which conditions the single auto-regen server-side, A5). Replaces the
// per-candidate scorecards for tutors; admins keep the #630 per-candidate surface.
function ComparativeBar({ pendingCount, selectedId, dangerFlagIds, onDecide }) {
  const [confirmPick, setConfirmPick] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [category, setCategory] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const fire = (body) => {
    setBusy(true);
    setError(null);
    Promise.resolve(onDecide(body))
      .then((res) => {
        if (!mountedRef.current) return;
        if (res && res.error) setError("Couldn’t save the decision — please retry.");
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setBusy(false);
        setConfirmPick(false);
        setRejectOpen(false);
      });
  };

  const rejectBody = buildGroupDecision({
    type: "REJECT_SET", category, note, dangerFlagIds,
  });
  const pickBody = buildGroupDecision({
    type: "PICK_BEST", winnerId: selectedId, dangerFlagIds,
  });

  return (
    <div className="rq-cmp-bar">
      <Typography variant="body2" className="rq-cmp-lede">
        Pick the best candidate — or reject the whole set with a reason.
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="primary"
        className="rq-cmp-confirm"
        disabled={!pickBody || busy}
        onClick={() => setConfirmPick(true)}
      >
        Confirm best pick
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        className="rq-cmp-rejectall"
        disabled={busy || pendingCount === 0}
        onClick={() => { setCategory(null); setNote(""); setRejectOpen(true); }}
      >
        Reject all
      </Button>
      {error && <Typography color="error" variant="caption" className="rq-cmp-error">{error}</Typography>}

      <Dialog open={confirmPick} onClose={() => (busy ? null : setConfirmPick(false))} aria-labelledby="rq-cmp-pick-title">
        <DialogTitle id="rq-cmp-pick-title">Approve the selected candidate?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The selected candidate is approved as this question's image; the other
            {" "}{Math.max(0, pendingCount - 1)} pending candidate(s) are rejected by comparison.
            This decision is final (an admin reset can reopen it).
          </DialogContentText>
          {/* P0-1d: the winner is one the reviewer danger-flagged in-flight — warn (don't block)
              so approving your own safety-flagged image is never silent. */}
          {winnerDangerFlagged(selectedId, dangerFlagIds) && (
            <DialogContentText className="rq-cmp-pick-danger-warn" role="alert">
              ⚑ You are approving an image you flagged for a clinical-safety concern.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPick(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={busy || !pickBody} onClick={() => fire(pickBody)}>
            {busy ? "Working…" : "Approve best pick"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => (busy ? null : setRejectOpen(false))} aria-labelledby="rq-cmp-reject-title">
        <DialogTitle id="rq-cmp-reject-title">Reject the whole set?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All pending candidates are rejected. Your reason conditions ONE automatic
            regeneration, which returns to your queue.
          </DialogContentText>
          <div className="rq-cmp-cats">
            {REJECT_CATEGORIES.map((c) => (
              // S5/P0-2/A4: a category's `hint` (the DIMENSION_HINTS line, e.g. ANSWER_LEAKAGE)
              // rides the chip as a tooltip; categories without a hint show no tooltip.
              <Tooltip key={c.key} title={c.hint || ""} placement="top">
                <Chip
                  size="small"
                  label={c.label}
                  className={`rq-cmp-cat ${category === c.key ? "rq-cmp-cat--on" : ""}`}
                  color={category === c.key ? "primary" : "default"}
                  onClick={() => setCategory(c.key)}
                />
              </Tooltip>
            ))}
          </div>
          <TextField
            className="rq-cmp-note"
            placeholder="One-line reason (required)"
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: 200 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="secondary" disabled={busy || !rejectBody} onClick={() => fire(rejectBody)}>
            {busy ? "Working…" : "Reject all candidates"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Admin-only reset confirmation (AD-3): captures a reason, then reopens the whole
// question. AD-6 (a published/retracted candidate → 409) is surfaced by the page.
function ResetDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setReason("");
      setBusy(false);
    }
  }, [open]);
  // A successful reset bumps the page's resetNonce, which remounts the ReviewPanel
  // (and this dialog) BEFORE onConfirm's promise settles — so the .finally's
  // setBusy/onClose would land on an unmounted tree. Guard on a mounted ref: on
  // success we're already gone (skip); on failure (409/error, no remount) we're
  // still mounted and close normally.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const confirm = () => {
    setBusy(true);
    Promise.resolve(onConfirm(reason)).finally(() => {
      if (!mountedRef.current) return;
      setBusy(false);
      onClose();
    });
  };
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="rq-reset-title">
      <DialogTitle id="rq-reset-title">Reset this question?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Reopens every candidate for fresh rating. Prior ratings are preserved (archived,
          never deleted) and the reset is audited.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          className="rq-reset-reason"
          placeholder="Reason for reset (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="secondary" variant="contained" className="rq-reset-confirm" disabled={busy || !reason.trim()} onClick={confirm}>
          Reset
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Admin-only reviewer-assignment panel (WI-2, AD-2). Mounted ONLY for admins (the
// caller gates on ROLE_ADMIN — never rendered for tutors, so the tutor roster never
// leaks to a tutor, AD-4). Shows the question's CURRENT assignees (from the admin-only
// assignment.assignedReviewerIds already on the payload), a SEARCHABLE tutor picker by
// NAME/email (mapped to id under the hood), and assign / revoke each behind a
// confirmation. Roster fetch + each mutation carry load + error states, surfaced
// non-destructively. The ACL is SERVER-enforced; this is a control surface only.
function AssignmentPanel({ subjectKey, group }) {
  const [open, setOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [rosterState, setRosterState] = useState("idle"); // idle | loading | ok | error
  const [assignees, setAssignees] = useState(() => currentAssigneeIds(group));
  const [query, setQuery] = useState("");
  const [confirm, setConfirm] = useState(null); // { type: "assign"|"revoke", id, label }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The roster/assign/revoke promises settle after the panel can unmount (question nav /
  // teardown); guard their .then/.catch/.finally so they skip setState when unmounted.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Reseed ONLY when the question or the SERVER assignment actually changes — never on
  // mere group object identity. The page rebuilds its group objects on every rating
  // render (decoratedItems recomputes on each localVotes change), and reseeding on that
  // would erase in-flight optimistic assignees / search text / a pending confirm.
  // Server ids are keyed as a joined string so an identical list never re-fires; ids are
  // UUIDs (no "|"), so the key round-trips losslessly.
  const serverAssigneesKey = currentAssigneeIds(group).join("|");
  useEffect(() => {
    setAssignees(serverAssigneesKey ? serverAssigneesKey.split("|") : []);
    setError(null);
    setConfirm(null);
    setQuery("");
  }, [subjectKey, serverAssigneesKey]);

  const loadRoster = useCallback(() => {
    setRosterState("loading");
    api
      .getUsersByRole("TUTOR")
      .then((res) => {
        if (!mountedRef.current) return;
        if (!res || (typeof res === "object" && !Array.isArray(res) && res.status)) {
          setRoster([]);
          setRosterState("error");
          return;
        }
        setRoster(normalizeRoster(res));
        setRosterState("ok");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setRoster([]);
        setRosterState("error");
      });
  }, []);

  const openPanel = () => {
    setOpen(true);
    if (rosterState === "idle" || rosterState === "error") loadRoster();
  };

  const doAssign = (tutor) => {
    setBusy(true);
    setError(null);
    api
      .assignReviewer(subjectKey, tutor.id)
      .then((res) => {
        if (!mountedRef.current) return;
        const outcome = assignmentOutcome(res);
        if (outcome === "ok") {
          setAssignees((prev) => (prev.includes(tutor.id) ? prev : [...prev, tutor.id]));
          setQuery("");
        } else {
          setError(outcome === "dark" ? "Assignment isn’t enabled on this server yet." : "Couldn’t assign — please retry.");
        }
      })
      .catch(() => mountedRef.current && setError("Couldn’t assign — please retry."))
      .finally(() => {
        if (!mountedRef.current) return;
        setBusy(false);
        setConfirm(null);
      });
  };

  const doRevoke = (id) => {
    setBusy(true);
    setError(null);
    api
      .revokeReviewer(subjectKey, id)
      .then((res) => {
        if (!mountedRef.current) return;
        const outcome = assignmentOutcome(res);
        if (outcome === "ok") {
          setAssignees((prev) => prev.filter((x) => x !== id));
        } else {
          setError(outcome === "dark" ? "Assignment isn’t enabled on this server yet." : "Couldn’t revoke — please retry.");
        }
      })
      .catch(() => mountedRef.current && setError("Couldn’t revoke — please retry."))
      .finally(() => {
        if (!mountedRef.current) return;
        setBusy(false);
        setConfirm(null);
      });
  };

  if (!open) {
    return (
      <div className="rq-assign-toggle">
        <Button size="small" variant="text" className="rq-assign-open" onClick={openPanel}>
          Manage reviewers{assignees.length ? ` (${assignees.length})` : ""}
        </Button>
      </div>
    );
  }

  const assignable = searchRoster(
    roster.filter((u) => !assignees.includes(u.id)),
    query
  );

  return (
    <div className="rq-assign">
      <div className="rq-assign-head">
        <Typography variant="subtitle2" className="rq-assign-title">Reviewer assignment</Typography>
        <Button size="small" variant="text" className="rq-assign-close" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>

      <Typography variant="caption" className="rq-assign-sub">Current reviewers</Typography>
      {assignees.length === 0 ? (
        <Typography variant="caption" className="rq-assign-none">No reviewers assigned yet.</Typography>
      ) : (
        <ul className="rq-assign-list">
          {assignees.map((id) => (
            <li key={id} className="rq-assign-row">
              <span className="rq-assign-name">{assigneeLabel(id, roster)}</span>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                className="rq-assign-revoke"
                disabled={busy}
                onClick={() => setConfirm({ type: "revoke", id, label: assigneeLabel(id, roster) })}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Typography variant="caption" className="rq-assign-sub">Assign a tutor</Typography>
      {rosterState === "loading" && (
        <div className="rq-assign-roster-loading"><CircularProgress size={18} /></div>
      )}
      {rosterState === "error" && (
        <div className="rq-assign-roster-error">
          <Typography variant="caption" color="error">Couldn’t load the tutor list.</Typography>
          <Button size="small" variant="outlined" className="rq-assign-roster-retry" onClick={loadRoster}>
            Retry
          </Button>
        </div>
      )}
      {rosterState === "ok" && (
        <>
          <TextField
            className="rq-assign-search"
            placeholder="Search tutors by name or email"
            variant="outlined"
            size="small"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {assignable.length === 0 ? (
            <Typography variant="caption" className="rq-assign-none">
              {roster.length === 0 ? "No tutors found." : "No matching tutors."}
            </Typography>
          ) : (
            <ul className="rq-assign-picker">
              {assignable.map((u) => (
                <li key={u.id} className="rq-assign-pick-row">
                  <span className="rq-assign-name">
                    {u.displayName || u.email || u.id}
                    {u.displayName && u.email ? <span className="rq-assign-email"> · {u.email}</span> : null}
                  </span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    className="rq-assign-add"
                    disabled={busy}
                    onClick={() => setConfirm({ type: "assign", id: u.id, label: u.displayName || u.email || u.id, tutor: u })}
                  >
                    Assign
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {error && <Typography color="error" variant="caption" className="rq-assign-error">{error}</Typography>}

      <Dialog open={!!confirm} onClose={() => (busy ? null : setConfirm(null))} aria-labelledby="rq-assign-confirm-title">
        <DialogTitle id="rq-assign-confirm-title">
          {confirm && confirm.type === "assign" ? "Assign reviewer?" : "Revoke reviewer?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm && confirm.type === "assign"
              ? `Assign ${confirm.label} to review this question?`
              : confirm
                ? `Remove ${confirm.label} from this question's reviewers?`
                : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm && confirm.type === "assign" ? "primary" : "secondary"}
            className="rq-assign-confirm-btn"
            disabled={busy}
            onClick={() => {
              if (!confirm) return;
              if (confirm.type === "assign") doAssign(confirm.tutor);
              else doRevoke(confirm.id);
            }}
          >
            {busy ? "Working…" : confirm && confirm.type === "assign" ? "Assign" : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// The N-up per-candidate review panel for one question (replaces the retired A/B
// comparative ComparePanel). Renders ALL candidates, each independently rated;
// admin reset; keyboard ←/→ nav + Esc-zoom.
function ReviewPanel({
  group, isAdmin, viewerId, readOnly, onRate, onDecide, onReset, onSuitability, onComplete,
  onPrev, onNext, canPrev, canNext, lightboxOpen, onZoom,
}) {
  const [resetOpen, setResetOpen] = useState(false);
  const cands = (group.candidates || []).filter(Boolean);
  const single = cands.length === 1;
  const bestId = bestItemId(group);
  const anyFinalized = cands.some(isItemFinalized);
  const authorized = isAuthorizedToRate(group, { isAdmin, viewerId });
  // A2: authorized TUTORS get ONE comparative decision per group; admins keep the
  // #630 per-candidate surface (they are not the review-burden case). The panel is
  // remounted per question (keyed by subjectKey upstream), so this state never bleeds.
  const pendingCands = cands.filter(isItemPending);
  const comparative = !isAdmin && authorized && !readOnly && pendingCands.length > 0;
  const [pickedId, setPickedId] = useState(null);
  const [dangerIds, setDangerIds] = useState(() => new Set());
  // S1.2/P2-6 honest mobile model: on <=640px a multi-candidate group renders a sticky
  // all-candidates thumb strip + ONE full-width focused card (tap a thumb to switch).
  // The panel is remounted per question (keyed upstream), so focus starts at 0 fresh.
  const isMobile = useIsMobile();
  const [focusIdx, setFocusIdx] = useState(0);
  const focus = Math.min(focusIdx, Math.max(0, cands.length - 1));
  const mobileCompare = isMobile && cands.length > 1;
  const toggleDanger = (id) => setDangerIds((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Prune stale selection/danger state when the PENDING set actually changes (a
  // same-question refetch keeps this instance mounted): a flag or pick must never
  // silently reference a candidate that is no longer pending. Keyed on content, not
  // object identity (the P2-B reseed discipline).
  const pendingIdsKey = pendingCands.map((c) => c.id).sort().join("|");
  useEffect(() => {
    const allowed = new Set(pendingIdsKey ? pendingIdsKey.split("|") : []);
    setPickedId((cur) => (cur && !allowed.has(cur) ? null : cur));
    setDangerIds((cur) => {
      const next = new Set([...cur].filter((id) => allowed.has(id)));
      return next.size === cur.size ? cur : next;
    });
  }, [pendingIdsKey]);

  // Keyboard: ←/→ navigate, Esc closes the zoom lightbox (owned by the lightbox).
  const shortcutRef = useRef(null);
  shortcutRef.current = (e) => {
    const action = resolveShortcut(
      { key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, editable: isEditableTarget(e.target) },
      { lightboxOpen: Boolean(lightboxOpen) }
    );
    if (!action) return;
    if (action.type === SHORTCUT.PREV) {
      e.preventDefault();
      if (onPrev) onPrev();
    } else if (action.type === SHORTCUT.NEXT) {
      e.preventDefault();
      if (onNext) onNext();
    }
  };
  useEffect(() => {
    const handler = (e) => shortcutRef.current && shortcutRef.current(e);
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <Paper className="rq-panel rq-panel--workspace" elevation={1}>
      <div className="rq-surface-main">
        <div className="rq-stemband">
          <PlacementBanner group={group} />
          <QuestionDetail subjectKey={group.subjectKey} />
          {/* S3-U1: the reference strip sits beside the candidate grid, shared by both roles. */}
          <ReferencePanel group={group} isAdmin={isAdmin} onZoom={onZoom} />
        </div>
        <div className="rq-media">
          <div className="rq-panel-head">
            {isAdmin && <QuestionIdBadge id={group.subjectKey} />}
            {/* S1-U1: held (below-floor) groups no longer render inline in the work grid —
                they leave the review work-list and surface in the admin-only NEEDS
                GENERATION rail section + read-only inspection view. The former inline
                "held — below candidate floor" chip is retired. */}
            {readOnly && <Chip size="small" label="decision already made" className="rq-chip-done rq-chip-readonly" />}
            {!readOnly && !authorized && (
              <Chip size="small" label="view only — not assigned" className="rq-chip-done rq-chip-viewonly" />
            )}
          </div>
          <button
            type="button"
            className="rq-chevron rq-chevron--prev"
            aria-label="Previous question"
            disabled={canPrev === false}
            onClick={() => onPrev && onPrev()}
          >
            ‹
          </button>
          <button
            type="button"
            className="rq-chevron rq-chevron--next"
            aria-label="Next question"
            disabled={canNext === false}
            onClick={() => onNext && onNext()}
          >
            ›
          </button>
          {/* AD-1/AD-5: every candidate renders in its own cell (decided included),
              never a top-2 slice or an empty placeholder pane. P2-6: on mobile the cells
              become a sticky all-candidates thumb strip + ONE focused card (an honest
              comparison model — never three unjudgeable 120px columns); on desktop the
              3-candidate case pins to a strict 3-up row (rq-compare--n3). */}
          {(() => {
            const card = (c, i) => (
              <CandidateCard
                key={c.id}
                candidate={c}
                index={i}
                total={cands.length}
                editable={!comparative && !readOnly && isCandidateEditable(c, group, { isAdmin, viewerId })}
                showModel={isAdmin}
                bestId={bestId}
                onZoom={onZoom}
                onSave={(body) => onRate(c.id, body, group)}
                comparative={comparative}
                comparativeSelected={comparative && pickedId === c.id}
                onComparativeSelect={setPickedId}
                dangerFlagged={dangerIds.has(c.id)}
                onToggleDanger={toggleDanger}
              />
            );
            if (mobileCompare) {
              return (
                <>
                  <div className="rq-thumbstrip">
                    {cands.map((c, i) => (
                      <CandidateThumb
                        key={c.id}
                        candidate={c}
                        index={i}
                        total={cands.length}
                        current={i === focus}
                        onFocus={() => setFocusIdx(i)}
                      />
                    ))}
                  </div>
                  <div className="rq-compare rq-compare--focused">
                    {card(cands[focus], focus)}
                  </div>
                </>
              );
            }
            return (
              <div className={`rq-compare ${single ? "rq-compare--single" : "rq-compare--multi"}${cands.length === 3 ? " rq-compare--n3" : ""}`}>
                {cands.map(card)}
              </div>
            );
          })()}
        </div>

        {isAdmin && <MetadataPanel group={group} />}
      </div>

      <div className="rq-actionbar">
        {readOnly && (
          <Typography variant="caption" className="rq-readonly-note">
            Decision already made — this question's review is closed and re-voting is
            disabled.
          </Typography>
        )}
        {isAdmin && (
          <div className="rq-admin-tools">
            {anyFinalized && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                className="rq-reset-btn"
                onClick={() => setResetOpen(true)}
              >
                Reset question
              </Button>
            )}
            <AssignmentPanel subjectKey={group.subjectKey} group={group} />
          </div>
        )}
        {comparative && (
          <ComparativeBar
            pendingCount={pendingCands.length}
            selectedId={pickedId}
            dangerFlagIds={dangerIds}
            onDecide={(body) => onDecide(group, body)}
          />
        )}
        {!readOnly && authorized && (
          <SuitabilitySection
            subjectKey={group.subjectKey}
            disabled={false}
            kindHint={cands[0] && cands[0].payload && (cands[0].payload.tableContent || cands[0].payload.tableHtml) ? "table" : "image"}
            onSuitability={onSuitability}
            onComplete={() => onComplete(group)}
            onBusyChange={() => {}}
            onOpenChange={() => {}}
          />
        )}
      </div>

      <ResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={(reason) => onReset(group.subjectKey, reason)}
      />
    </Paper>
  );
}

// S1-U1 read-only INSPECTION view for a NEEDS-GENERATION (held, below-floor) group. A held
// group is NOT review work: it is awaiting a generation top-up. This surface shows the
// existing candidates read-only (no rating / comparative / decision affordances at all),
// a banner with the pending/floor gap + the top-up request state, and — for admins — the
// assignment panel + reset, which stay reachable. Candidates are fetched via the
// single-question read (the needs-generation payload carries only the gap, not the images).
function NeedsGenerationInspector({ entry, isAdmin, onReset, onZoom }) {
  const [state, setState] = useState({ status: "loading", group: null });
  const [resetOpen, setResetOpen] = useState(false);
  const subjectKey = entry.subjectKey;
  useEffect(() => {
    let active = true;
    setState({ status: "loading", group: null });
    api
      .getReviewQuestion(subjectKey)
      .then((res) => {
        if (!active) return;
        if (!res || res.status || res.errStatus) {
          setState({ status: "error", group: null });
          return;
        }
        setState({ status: "ok", group: adaptQuestionResponse(res) });
      })
      .catch(() => active && setState({ status: "error", group: null }));
    return () => { active = false; };
  }, [subjectKey]);

  const group = state.group;
  const cands = ((group && group.candidates) || []).filter(Boolean);
  const single = cands.length === 1;

  return (
    <Paper className="rq-panel rq-panel--inspect" elevation={1}>
      <div className="rq-inspect-banner">
        <Typography variant="subtitle2" className="rq-inspect-title">Awaiting generation top-up</Typography>
        <Typography variant="body2" className="rq-inspect-sub">
          This question has {entry.pendingCount} of {entry.floor} candidates — it is held out
          of the review queue until the generation pipeline tops it up. No review action is
          needed here.
        </Typography>
        <div className="rq-inspect-meta">
          <Chip size="small" className="rq-inspect-chip" label={`${entry.pendingCount} / ${entry.floor} candidates`} />
          <Chip
            size="small"
            className={`rq-inspect-chip rq-inspect-chip--${((entry.topUpRequest && entry.topUpRequest.status) || "none").toLowerCase()}`}
            label={topUpStatusLabel(entry.topUpRequest)}
          />
        </div>
      </div>

      <div className="rq-stemband">
        <QuestionDetail subjectKey={subjectKey} />
      </div>

      {state.status === "loading" ? (
        <div className="rq-inspect-loading"><CircularProgress size={22} /></div>
      ) : state.status === "error" ? (
        <Typography variant="caption" className="rq-inspect-note">
          Couldn’t load the existing candidates — the generation state above still applies.
        </Typography>
      ) : cands.length === 0 ? (
        <Typography variant="caption" className="rq-inspect-note">No candidates generated yet.</Typography>
      ) : (
        <div className={`rq-compare ${single ? "rq-compare--single" : "rq-compare--multi"}`}>
          {cands.map((c, i) => (
            <CandidateCard
              key={c.id}
              candidate={c}
              index={i}
              total={cands.length}
              editable={false}
              showModel={isAdmin}
              bestId={null}
              onZoom={onZoom}
              onSave={() => {}}
              comparative={false}
            />
          ))}
        </div>
      )}

      {isAdmin && (
        <div className="rq-actionbar">
          <div className="rq-admin-tools">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              className="rq-reset-btn"
              onClick={() => setResetOpen(true)}
            >
              Reset question
            </Button>
            {group && <AssignmentPanel subjectKey={subjectKey} group={group} />}
          </div>
        </div>
      )}

      <ResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={(reason) => onReset(subjectKey, reason)}
      />
    </Paper>
  );
}

// Full-viewport zoom lightbox with a focus trap. Esc and a backdrop/close click
// dismiss it; focus moves to the close button on open and returns on close.
// S1.2/P2-7 deep zoom: fit-to-screen is not enough for dense medical diagrams (staging
// tables, labeled anatomy — the exact label-fidelity the bakeoff scored), so the image
// pan/zooms via CSS transform: wheel or +/− controls to zoom (clamped 1–8×), drag to pan
// while zoomed, double-click to toggle 1↔2.5×, Reset returns to fit. Zoom state lives per
// open (mounting resets it).
const LIGHTBOX_MIN_SCALE = 1;
const LIGHTBOX_MAX_SCALE = 8;
const LIGHTBOX_WHEEL_STEP = 1.2;
const LIGHTBOX_DBLCLICK_SCALE = 2.5;
function Lightbox({ src, alt, onClose, triggerRef }) {
  const closeRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const dragRef = useRef(null); // { startX, startY, baseX, baseY } while dragging
  const clampScale = (s) => Math.min(LIGHTBOX_MAX_SCALE, Math.max(LIGHTBOX_MIN_SCALE, s));
  const zoomBy = (factor) => setView((v) => {
    const scale = clampScale(v.scale * factor);
    // Back at fit → recentre (a stale pan offset on a fit image looks broken).
    return scale === 1 ? { scale: 1, x: 0, y: 0 } : { ...v, scale };
  });
  const resetView = () => setView({ scale: 1, x: 0, y: 0 });

  useEffect(() => {
    const restore = triggerRef && triggerRef.current;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => {
      const action = resolveShortcut(
        { key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, editable: false },
        { lightboxOpen: true }
      );
      if (action && action.type === SHORTCUT.CLOSE_ZOOM) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        if (closeRef.current) closeRef.current.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (restore && typeof restore.focus === "function") restore.focus();
    };
  }, [onClose, triggerRef]);

  // Pan only while zoomed in — at fit scale a drag is meaningless (and a click must
  // still reach the backdrop-close path untouched).
  const onMouseDown = (e) => {
    if (view.scale === 1) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: view.x, baseY: view.y };
  };
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      setView((v) => ({ ...v, x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) }));
    };
    const onUp = () => { dragRef.current = null; };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <div
      className="rq-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged candidate image"
      onClick={onClose}
    >
      <button type="button" ref={closeRef} className="rq-lightbox-close" onClick={onClose} aria-label="Close">
        ×
      </button>
      <div className="rq-lightbox-controls" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="rq-lightbox-ctrl" aria-label="Zoom in" onClick={() => zoomBy(LIGHTBOX_WHEEL_STEP)}>+</button>
        <button type="button" className="rq-lightbox-ctrl" aria-label="Zoom out" onClick={() => zoomBy(1 / LIGHTBOX_WHEEL_STEP)}>−</button>
        <button type="button" className="rq-lightbox-ctrl" aria-label="Reset zoom" onClick={resetView}>fit</button>
        <span className="rq-lightbox-scale" aria-hidden="true">{Math.round(view.scale * 100)}%</span>
      </div>
      <img
        className={`rq-lightbox-img${view.scale > 1 ? " rq-lightbox-img--zoomed" : ""}`}
        src={src}
        alt={alt || "candidate visual (enlarged)"}
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => zoomBy(e.deltaY < 0 ? LIGHTBOX_WHEEL_STEP : 1 / LIGHTBOX_WHEEL_STEP)}
        onDoubleClick={() => setView((v) => (v.scale === 1 ? { ...v, scale: LIGHTBOX_DBLCLICK_SCALE } : { scale: 1, x: 0, y: 0 }))}
        onMouseDown={onMouseDown}
      />
    </div>
  );
}

export default function ReviewQueuePage() {
  const role = window.localStorage.getItem("role");
  const canReview = role === "ROLE_ADMIN" || role === "ROLE_TUTOR";
  // Model/metadata is admin-only (AD-4); ROLE_ADMIN is the privileged tier.
  const showModel = role === "ROLE_ADMIN";
  const viewerId = window.localStorage.getItem("userId");

  const [items, setItems] = useState([]);
  // QUESTION-unit pagination flag: totalElements on the all-candidates page counts
  // questions, so "more on server" is decided against the fetched QUESTION count
  // (moreQuestionsOnServer), never against the flattened candidate-card count.
  const [moreOnServer, setMoreOnServer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [current, setCurrent] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [kindFilter, setKindFilter] = useState("ALL");
  const [snackbar, setSnackbar] = useState(null);
  const [decidedItems, setDecidedItems] = useState([]);
  const [viewCompletedKey, setViewCompletedKey] = useState(null);
  // S1-U1: the admin-only needs-generation partition (held below-floor groups) from the
  // page payload, and the held group currently open in the read-only inspection view.
  const [needsGeneration, setNeedsGeneration] = useState([]);
  const [inspectKey, setInspectKey] = useState(null);
  // Optimistic per-candidate saved ratings (itemId → { decision, isBest, comment })
  // so a saved rating shows immediately (AD-1) without a reload.
  const [localVotes, setLocalVotes] = useState(() => new Map());
  // AD-7 (save-is-final): a saved rating finalizes its candidate immediately
  // (threshold=1 auto-promotion), so on a successful save we optimistically mark the
  // item decided (itemId → "APPROVED"/"REJECTED") → it renders read-only with no edit
  // control. The only reopen is an admin reset.
  const [finalizedLocal, setFinalizedLocal] = useState(() => new Map());
  // Questions an admin has reset this session → their candidates render PENDING and
  // editable again (AD-3), without waiting on a refetch.
  const [reopenedKeys, setReopenedKeys] = useState(() => new Set());
  // Bumped on every successful reset so the panel remounts and reseeds its controls.
  const [resetNonce, setResetNonce] = useState(0);
  const zoomTriggerRef = useRef(null);
  // Async-lifecycle guard (mirrors the #629 P2.2 ResetDialog mountedRef): the queue
  // load + rate flows settle their promises after the page can unmount (nav away /
  // test teardown), so their .then/.catch/.finally must skip setState when unmounted.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const RAIL_OPEN_MIN_WIDTH = 1600;
  const railPinnedRef = useRef(false);
  const [railOpen, setRailOpen] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= RAIL_OPEN_MIN_WIDTH
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(min-width: ${RAIL_OPEN_MIN_WIDTH}px)`);
    const onChange = (e) => {
      if (!railPinnedRef.current) setRailOpen(e.matches);
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [RAIL_OPEN_MIN_WIDTH]);

  const loadQueue = useCallback(() => {
    setLoading(true);
    setError(null);
    // #584 P1: source the grid from the ALL-CANDIDATES page (GET /review/questions), not
    // the PENDING-only /queue worklist. Each returned question carries its FULL active
    // candidate set (decided included, AD-5), so a mixed (pending+decided) question renders
    // ALL its candidates in one N-up grid instead of only its pending cell.
    api
      .getReviewQuestionsPage({ type: REVIEW_TYPE, examType: EXAM_TYPE, page: 0, size: PAGE_SIZE })
      .then((res) => {
        if (!mountedRef.current) return;
        if (res && res.status === 404) {
          setDisabled(true);
          setItems([]);
          setMoreOnServer(false);
          setNeedsGeneration([]);
          return;
        }
        // Accept BOTH the real QuestionCandidatesPage {questions} and the legacy
        // flat {items} shape; anything else (error object / unexpected) surfaces.
        if (!res || res.status || !hasQuestionsPayload(res)) {
          setError("Could not load the review queue.");
          setSnackbar({ message: "Could not load the review queue.", onRetry: () => loadQueue() });
          setItems([]);
          setMoreOnServer(false);
          setNeedsGeneration([]);
          return;
        }
        setDisabled(false);
        setSnackbar(null);
        const { items: flatItems } = flattenQuestionsResponse(res);
        setItems(flatItems);
        setMoreOnServer(moreQuestionsOnServer(res));
        // Admin-only held-group partition (tutors → []); the rail's NEEDS GENERATION section.
        setNeedsGeneration(extractNeedsGeneration(res));
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setError("Could not load the review queue.");
        setSnackbar({ message: "Could not load the review queue.", onRetry: () => loadQueue() });
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  // Supplementary completed-rail feed (#584 P1): the all-candidates page already carries
  // every fully-decided question on the fetched page (→ workCompleted, with full candidate
  // sets), so this is now only a fallback that surfaces decided questions BEYOND that page.
  // It self-dedupes against the loaded candidate ids (filterDecidedItems), so it never
  // double-counts or re-splits a mixed question's candidates. (Contract permits a retained
  // status-filtered feed; the GRID's source is the all-candidates page.) Silent on dark/fail.
  const DECIDED_PAGE_SIZE = 25;
  const loadDecided = useCallback(() => {
    api
      .getReviewQueue({ type: REVIEW_TYPE, examType: EXAM_TYPE, status: "DECIDED", page: 0, size: DECIDED_PAGE_SIZE })
      .then((res) => {
        if (mountedRef.current && hasQuestionsPayload(res)) setDecidedItems(flattenQuestionsResponse(res).items);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (canReview) {
      loadQueue();
      loadDecided();
    }
  }, [canReview, loadQueue, loadDecided]);

  // Post one candidate's rating (the vote upsert). #578 write-guard: a vote to a
  // non-PENDING item → 409; an unassigned non-admin → 403. Resolves { error:true }
  // on failure so the control can keep its typed state.
  // A2 comparative decision (T-CMP U1): ONE POST per group decision; on success the
  // queue REFETCHES (server truth — the decided group leaves the work grid and the
  // floor/regen state comes back from the source, no local guesswork). Failures
  // surface non-destructively; the caller (ComparativeBar) shows the inline error.
  const onDecide = useCallback((group, body) => {
    return api
      .decideReviewQuestion(group.subjectKey, body)
      .then((res) => {
        const outcome = decisionOutcome(res);
        if (outcome === "ok") {
          if (mountedRef.current) {
            loadQueue();
            loadDecided();
          }
          return null;
        }
        if (mountedRef.current) setSnackbar({
          message:
            outcome === "conflict"
              ? "This question is already decided — reload to see the latest."
              : outcome === "dark"
                ? "Comparative decisions aren’t enabled on this server yet."
                : "Couldn’t save the decision — please retry.",
          onRetry: null,
        });
        return { error: true };
      })
      .catch(() => {
        if (mountedRef.current) setSnackbar({ message: "Couldn’t save the decision — please retry." });
        return { error: true };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadQueue, loadDecided]);

  const onRate = useCallback((itemId, body, group) => {
    return api
      .voteReviewItem(itemId, body)
      .then((res) => {
        // The vote can settle after unmount (nav / teardown); the caller still gets its
        // resolution (so RatingControl clears busy), but page setState is skipped.
        if (res && res.errStatus) {
          const s = String(res.errStatus);
          if (mountedRef.current) setSnackbar({
            message:
              s === "409"
                ? "This candidate is already decided — reload to see the latest."
                : s === "403"
                  ? "You're not assigned to rate this question."
                  : "Couldn’t save the rating — please retry.",
          });
          return { error: true };
        }
        if (!mountedRef.current) return null;
        setLocalVotes((prev) => {
          const next = new Map(prev);
          // isBest is ≤1 per question — if this candidate is flagged best, clear the
          // flag on its siblings locally so the UI matches the server's exclusivity.
          if (body.isBest && group) {
            groupItemIds(group).forEach((sid) => {
              if (sid !== itemId && next.has(sid)) {
                const v = next.get(sid);
                if (v && v.isBest) next.set(sid, { ...v, isBest: false });
              }
            });
          }
          next.set(itemId, { decision: body.decision, isBest: Boolean(body.isBest), comment: body.comment || "" });
          return next;
        });
        // AD-7: the save finalizes the candidate — flip it to its decided status so
        // the cell renders read-only (no further tutor edit; admin reset reopens).
        setFinalizedLocal((prev) => {
          const next = new Map(prev);
          next.set(itemId, body.decision === "APPROVE" ? "APPROVED" : "REJECTED");
          return next;
        });
        setSnackbar({ message: "Rating saved." });
        return null;
      })
      .catch(() => {
        if (mountedRef.current) setSnackbar({ message: "Couldn’t save the rating — please retry." });
        return { error: true };
      });
  }, []);

  // Admin reset (AD-3): reopens the whole question. AD-6 — a 409 (a candidate is
  // published/retracted) is surfaced non-destructively with NO optimistic reopen.
  const onReset = useCallback((subjectKey, reason) => {
    return api
      .resetReviewQuestion(subjectKey, buildResetRequest(reason))
      .then((res) => {
        const outcome = resetOutcome(res);
        if (outcome === "ok") {
          setReopenedKeys((prev) => new Set(prev).add(subjectKey));
          const clearForSubject = (map) => {
            const next = new Map(map);
            (items || []).forEach((it) => {
              if (it && it.subjectKey === subjectKey) next.delete(it.id);
            });
            return next;
          };
          setLocalVotes(clearForSubject);
          setFinalizedLocal(clearForSubject);
          setResetNonce((n) => n + 1);
          setViewCompletedKey(null);
          setSnackbar({ message: "Question reopened — candidates are editable again." });
        } else if (outcome === "conflict") {
          setSnackbar({ message: "Can't reset — a candidate is published/retracted." });
        } else if (outcome === "dark") {
          setSnackbar({ message: "Reset isn't enabled on this server yet." });
        } else {
          setSnackbar({ message: "Reset failed — please retry." });
        }
        return res;
      })
      .catch(() => {
        setSnackbar({ message: "Reset failed — please retry." });
      });
  }, [items]);

  const onSuitability = useCallback((subjectKey, state) => {
    const body = buildSuitabilityRequest(subjectKey, EXAM_TYPE, state);
    return api
      .markSuitability(body)
      .then((res) => suitabilityOutcome(res))
      .catch(() => "error");
  }, []);

  const onComplete = useCallback(() => {
    loadQueue();
    loadDecided();
    setViewCompletedKey(null);
  }, [loadQueue, loadDecided]);

  // Apply optimistic saved ratings + admin reopen onto the loaded items before
  // grouping, so the view reflects rate/edit/reset without a refetch.
  const decoratedItems = useMemo(
    () =>
      (items || []).map((it) => {
        const reopened = reopenedKeys.has(it.subjectKey);
        const lv = localVotes.get(it.id);
        const fl = finalizedLocal.get(it.id);
        let reviewStatus = it.reviewStatus;
        let myVote = it.myVote;
        if (reopened) {
          // Admin reset wins: the candidate is PENDING and editable again.
          reviewStatus = "PENDING";
          myVote = lv !== undefined ? lv : null;
        } else {
          if (lv !== undefined) myVote = lv;
          if (fl !== undefined) reviewStatus = fl; // AD-7: saved rating finalized it
        }
        return { ...it, reviewStatus, myVote };
      }),
    [items, reopenedKeys, localVotes, finalizedLocal]
  );

  const allGroups = useMemo(() => groupBySubject(decoratedItems), [decoratedItems]);
  const { work: workGroups, completed: workCompleted } = useMemo(
    () => splitGroupsByState(allGroups),
    [allGroups]
  );
  const filteredGroups = useMemo(() => filterGroupsByKind(workGroups, kindFilter), [workGroups, kindFilter]);
  const kindOptions = useMemo(() => presentVisualKinds(workGroups), [workGroups]);

  const workItemIds = useMemo(() => new Set(items.map((it) => it && it.id)), [items]);
  const decidedGroups = useMemo(
    () => groupBySubject(filterDecidedItems(decidedItems, workItemIds)),
    [decidedItems, workItemIds]
  );
  const completedGroups = useMemo(() => {
    const workKeys = new Set(workGroups.map((g) => g.subjectKey));
    const completedFromWorkKeys = new Set(workCompleted.map((g) => g.subjectKey));
    return [
      ...workCompleted,
      ...decidedGroups.filter(
        (g) => !workKeys.has(g.subjectKey) && !completedFromWorkKeys.has(g.subjectKey)
      ),
    ];
  }, [workGroups, workCompleted, decidedGroups]);

  const viewedCompletedGroup = useMemo(
    () => (viewCompletedKey ? completedGroups.find((g) => g.subjectKey === viewCompletedKey) || null : null),
    [viewCompletedKey, completedGroups]
  );

  const progressLine = groupProgress({
    reviewedGroups: completedGroups.length,
    toReviewGroups: filteredGroups.length,
    moreOnServer,
  });

  useEffect(() => {
    if (kindFilter !== "ALL" && workGroups.length > 0 && !kindOptions.includes(kindFilter)) {
      setKindFilter("ALL");
    }
  }, [kindFilter, kindOptions, workGroups.length]);

  useEffect(() => {
    setCurrent((c) => (filteredGroups.length === 0 ? 0 : Math.min(c, filteredGroups.length - 1)));
  }, [filteredGroups.length]);

  const closeZoom = useCallback(() => setZoom(null), []);
  const goPrev = useCallback(() => {
    if (viewCompletedKey || inspectKey) {
      setViewCompletedKey(null);
      setInspectKey(null);
      return;
    }
    setCurrent((c) => Math.max(0, c - 1));
  }, [viewCompletedKey, inspectKey]);
  const goNext = useCallback(() => {
    if (viewCompletedKey || inspectKey) {
      setViewCompletedKey(null);
      setInspectKey(null);
      return;
    }
    setCurrent((c) => Math.min(filteredGroups.length - 1, c + 1));
  }, [viewCompletedKey, inspectKey, filteredGroups.length]);
  const pickFilter = useCallback((k) => { setKindFilter(k); setCurrent(0); setViewCompletedKey(null); setInspectKey(null); }, []);
  const selectWork = useCallback((idx) => { setViewCompletedKey(null); setInspectKey(null); setCurrent(idx); }, []);
  const selectCompleted = useCallback((key) => { setInspectKey(null); setViewCompletedKey(key); }, []);
  // S1-U1: open a held group's read-only inspection view (mutually exclusive with the work
  // grid / completed view).
  const selectNeedsGen = useCallback((key) => { setViewCompletedKey(null); setInspectKey(key); }, []);
  const refreshAll = useCallback(() => {
    loadQueue();
    loadDecided();
  }, [loadQueue, loadDecided]);

  useEffect(() => {
    if (viewCompletedKey && !viewedCompletedGroup) setViewCompletedKey(null);
  }, [viewCompletedKey, viewedCompletedGroup]);

  // The held group currently open in the inspection view; clears if a reload removed it
  // (e.g. the pipeline topped it up so it re-entered the review queue).
  const inspectedEntry = useMemo(
    () => (inspectKey ? needsGeneration.find((e) => e.subjectKey === inspectKey) || null : null),
    [inspectKey, needsGeneration]
  );
  useEffect(() => {
    if (inspectKey && !inspectedEntry) setInspectKey(null);
  }, [inspectKey, inspectedEntry]);

  if (!canReview) {
    return (
      <Box className="body-cont-over rq-page">
        <Menu />
        <div className="rq-shell">
          <div className="rq-denied">
            <Typography variant="h6">Image review is available to tutors and admins.</Typography>
          </div>
        </div>
      </Box>
    );
  }

  const activeGroup = viewedCompletedGroup || filteredGroups[current] || null;

  return (
    <Box className="body-cont-over rq-page">
      <Menu />
      <Backdrop open={loading} style={{ zIndex: 1400, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="rq-shell rq-workspace">
        <div className="rq-topbar">
          <Typography variant="h6" className="rq-topbar-title">
            ABSITE image review
          </Typography>
          {!disabled && filteredGroups.length > 0 && !viewCompletedKey && !inspectKey && (
            <span className="rq-nav-pos">
              Question {current + 1} of {filteredGroups.length}
              {filteredGroups[current] && isKnownVisualKind(filteredGroups[current].visualKind) &&
                ` · ${visualKindLabel(filteredGroups[current].visualKind)}`}
            </span>
          )}
          {viewedCompletedGroup && !inspectKey && (
            <span className="rq-nav-pos">
              {isGroupReadOnly(viewedCompletedGroup)
                ? "Completed · read-only"
                : "Completed · you can update your review"}
            </span>
          )}
          {inspectedEntry && (
            <span className="rq-nav-pos">Needs generation · read-only</span>
          )}
          <Button size="small" variant="outlined" className="rq-refresh" disabled={loading} onClick={refreshAll}>
            Refresh
          </Button>
        </div>
        <Typography variant="caption" className="rq-sub">
          {showModel
            ? "Rate each AI-generated candidate against the question — usable or not — and optionally flag the best of the set. Ratings are per candidate; use → to leave a question for later."
            : "Choose the best candidate for each question — or reject the whole set with a reason. Flag ⚑ any image with a clinical-safety concern; use → to leave a question for later."}
        </Typography>

        {error && (
          <Typography color="error" className="rq-error">{error}</Typography>
        )}

        {!loading && disabled && (
          <Typography className="rq-empty">Image review is not enabled yet.</Typography>
        )}

        {!loading && !disabled && workGroups.length === 0 && completedGroups.length === 0
          && needsGeneration.length === 0 && !error && (
          <Typography className="rq-empty">No candidates awaiting review. 🎉</Typography>
        )}

        {!loading && !disabled && (workGroups.length > 0 || completedGroups.length > 0 || needsGeneration.length > 0) && (
          <div className="rq-workspace-body">
            <StatusRail
              workGroups={filteredGroups}
              completedGroups={completedGroups}
              needsGenerationGroups={needsGeneration}
              currentKey={inspectKey || (viewedCompletedGroup || filteredGroups[current] || {}).subjectKey}
              onSelectWork={selectWork}
              onSelectCompleted={selectCompleted}
              onSelectNeedsGen={selectNeedsGen}
              collapsed={!railOpen}
              onToggle={() => {
                railPinnedRef.current = true;
                setRailOpen((o) => !o);
              }}
              locked={false}
              isAdmin={showModel}
              progressLine={items.length > 0 || completedGroups.length > 0 ? progressLine : null}
              filterRow={
                showModel && kindOptions.length > 1 ? (
                  <div className="rq-kind-filter">
                    <Chip
                      size="small"
                      label={`All (${workGroups.length})`}
                      clickable
                      aria-pressed={kindFilter === "ALL"}
                      variant={kindFilter === "ALL" ? "default" : "outlined"}
                      className={`rq-filter-chip ${kindFilter === "ALL" ? "rq-filter-chip--on" : ""}`}
                      onClick={() => pickFilter("ALL")}
                    />
                    {kindOptions.map((k) => (
                      <Chip
                        key={k}
                        size="small"
                        label={`${visualKindLabel(k)} (${workGroups.filter((g) => normalizeVisualKind(g.visualKind) === k).length})`}
                        clickable
                        aria-pressed={kindFilter === k}
                        variant={kindFilter === k ? "default" : "outlined"}
                        className={`rq-filter-chip ${kindFilter === k ? "rq-filter-chip--on" : ""}`}
                        onClick={() => pickFilter(k)}
                      />
                    ))}
                  </div>
                ) : null
              }
            />

            <div className="rq-surface-col">
              {inspectedEntry ? (
                <>
                  <NeedsGenerationInspector
                    key={"inspect:" + inspectedEntry.subjectKey + ":" + resetNonce}
                    entry={inspectedEntry}
                    isAdmin={showModel}
                    onReset={onReset}
                    onZoom={(src, alt, trigger) => {
                      zoomTriggerRef.current = trigger || null;
                      setZoom({ src, alt });
                    }}
                  />
                  <Typography variant="caption" className="rq-kbd-hints">
                    ←→ back to queue · Esc close zoom
                  </Typography>
                </>
              ) : activeGroup ? (
                <>
                  <ReviewPanel
                    key={activeGroup.subjectKey + (viewCompletedKey ? ":done" : "") + ":" + resetNonce}
                    group={activeGroup}
                    isAdmin={showModel}
                    viewerId={viewerId}
                    readOnly={viewedCompletedGroup ? isGroupReadOnly(viewedCompletedGroup) : false}
                    onRate={onRate}
                    onDecide={onDecide}
                    onReset={onReset}
                    onSuitability={onSuitability}
                    onComplete={onComplete}
                    onPrev={goPrev}
                    onNext={goNext}
                    canPrev={viewCompletedKey ? true : current > 0}
                    canNext={viewCompletedKey ? true : current < filteredGroups.length - 1}
                    lightboxOpen={!!zoom}
                    onZoom={(src, alt, trigger) => {
                      zoomTriggerRef.current = trigger || null;
                      setZoom({ src, alt });
                    }}
                  />
                  <Typography variant="caption" className="rq-kbd-hints">
                    ←→ navigate · Esc close zoom
                  </Typography>
                </>
              ) : (
                <Typography className="rq-empty">
                  {kindFilter !== "ALL"
                    ? `No ${visualKindLabel(kindFilter)} questions in the queue.`
                    : "No candidates awaiting review. 🎉"}
                </Typography>
              )}
            </div>
          </div>
        )}
      </div>

      {zoom && (
        <Lightbox
          src={zoom.src}
          alt={zoom.alt}
          onClose={closeZoom}
          triggerRef={zoomTriggerRef}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={snackbar && snackbar.onRetry ? null : 4000}
        onClose={(e, reason) => {
          if (reason === "clickaway") return;
          setSnackbar(null);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={snackbar ? snackbar.message : ""}
        action={
          snackbar && snackbar.onRetry ? (
            <Button
              color="secondary"
              size="small"
              onClick={() => {
                const retry = snackbar.onRetry;
                setSnackbar(null);
                retry();
              }}
            >
              Retry
            </Button>
          ) : null
        }
      />
    </Box>
  );
}
